import * as Sentry from '@sentry/remix';
import { userStore } from '~/lib/stores/user';
import { setPreAdminUrl } from '~/lib/stores/netlify';

interface CreateSiteResponse {
  id: string;
  url: string;
}

export interface DeploymentResponse {
  id: string;
  deploy_id: string;
  sha: string | null;
  done: boolean;
  error: string | null;
  created_at: string;
  deploy_state: string;
  deploy_pending_review_reason: string;
  state: string;
  admin_url: string | null;
}

export async function createNetlifySite(accessToken: string, projectName: string): Promise<CreateSiteResponse> {
  return await Sentry.startSpan(
    {
      name: 'createNetlifySite',
      op: 'createNetlifySite',
    },
    async () => {
      const response = await fetch('https://api.netlify.com/api/v1/sites', {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${accessToken}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          name: `${projectName}`,
          ssl: true,
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to create Netlify site');
      }

      return response.json();
    },
  );
}

export async function deployToNetlify(accessToken: string, siteId: string, zipBlob: Blob): Promise<DeploymentResponse> {
  try {
    console.log('[netlify-api] Deploying to Netlify for siteId:', siteId, 'Zip size:', zipBlob.size);

    return await Sentry.startSpan(
      {
        name: 'deployToNetlify',
        op: 'deployToNetlify',
      },
      async () => {
        const response = await fetch(`/api/netlify-deploy`, {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${accessToken}`,
            'X-Site-Id': siteId,
            'Content-Type': 'application/zip',
          },
          body: zipBlob,
        });

        if (!response.ok) {
          const errorText = await response.text();
          console.error(`[netlify-api] Failed to deploy to Netlify: ${response.status}`, errorText);

          throw new Error(`Failed to deploy to Netlify: ${response.status} - ${errorText}`);
        }

        return response.json();
      },
    );
  } catch (error: any) {
    Sentry.captureException(error, {
      extra: {
        message: 'Error deploying to Netlify',
        siteId,
        zipBlobSize: zipBlob.size,
      },
    });

    console.log('error from deployToNetlify >', error.message);

    return error.message || 'Failed to create deployment';
  }
}

export async function checkDeploymentStatus(accessToken: string, deployId: string): Promise<DeploymentResponse> {
  console.log('[netlify-api] Checking deployment status for deployId:', deployId);

  try {
    return await Sentry.startSpan(
      {
        name: 'checkDeploymentStatus',
        op: 'checkDeploymentStatus',
      },
      async () => {
        const response = await fetch(`https://api.netlify.com/api/v1/deploys/${deployId}`, {
          headers: {
            Authorization: `Bearer ${accessToken}`,
            'Content-Type': 'application/json',
          },
        });

        if (!response.ok) {
          const errorText = await response.text();
          console.error(`[netlify-api] Failed to check deployment status: ${response.status}`, errorText);

          throw new Error(`Failed to check deployment status: ${response.status} - ${errorText}`);
        }

        const data = (await response.json()) as DeploymentResponse;
        console.log('[netlify-api] Deployment Status:', data.state);

        if (data.admin_url) {
          setPreAdminUrl(`${data.admin_url}/deploys/${deployId}`);
        }

        // Possible states: 'new', 'enqueued', 'building', 'ready', 'error'
        if (data.state === 'error') {
          console.error('[netlify-api] Deployment failed with error:', data.error || 'Unknown error');

          throw new Error(`Deployment failed: ${data.error || 'Unknown error'}`);
        }

        return data;
      },
    );
  } catch (error: any) {
    Sentry.captureException(error, {
      extra: {
        message: 'Error checking deployment status',
        deployId,
      },
    });
    throw error;
  }
}
